import React, { useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useAtom } from 'jotai';

import { CART_KEY } from '@/lib/constants';
import { useLocalStorage } from '@/lib/use-local-storage';
import { createCartSyncQueue } from '@/lib/cart-sync/cart-sync-queue';
import { deleteServerCart } from '@/lib/cart-sync/cart-sync-api';
import { serializeCartForSync } from '@/lib/cart-sync/serialize-cart-for-sync';
import { ICartProduct } from '@/types';
import { authorizationAtom, checkIsLoggedIn } from '@/store/authorization-atom';
import { verifiedResponseAtom } from '@/store/checkout';
import { useSettings } from '@/framework/settings';
import type { CartVerifyItemResponse } from '@/types';

import { cartReducer, initialState } from './cart.reducer';
import { getItem, inStock } from './cart.utils';
import type { UpdateItemInput } from './cart.utils';
import {
  clearCartSession,
  ensureCartSession,
} from '@/lib/cart-session';

function normalizeCartLineTimestamps(items: ICartProduct[]): ICartProduct[] {
  const now = new Date().toISOString();
  return (items ?? []).map((it) => ({
    ...it,
    cart_line_updated_at: it.cart_line_updated_at ?? now,
  }));
}

interface CartProviderState {
  items: ICartProduct[];
  settings?: any;
  isEmpty: boolean;
  totalItems: number;
  totalUniqueItems: number;
  total: number;
  meta?: Record<string, any> | null;
  language: string;
  addItemsToCart: (items: ICartProduct[]) => void;
  addItemToCart: (item: ICartProduct, quantity: number) => void;
  removeItemFromCart: (id: ICartProduct['id'], quantity?: number) => void;
  clearItemFromCart: (id: ICartProduct['id']) => void;
  getItemFromCart: (id: ICartProduct['id']) => any | undefined;
  isInCart: (id: ICartProduct['id']) => boolean;
  countItemsInCartById: (id: ICartProduct['id']) => number;
  isInStock: (id: ICartProduct['id']) => boolean;
  resetCart: () => void;
  hydrateMergedCart: (items: ICartProduct[]) => void;
  updateCartLanguage: (language: string) => void;
  updateItemPrices: (
    items: {
      id: string;
      price: number;
      sale_price: number;
      promotional_pack_unit_price?: number;
      product_prices?: any[];
    }[],
  ) => void;
  updateItem: (id: ICartProduct['id'], item: UpdateItemInput) => void;
  removePBMFromItem: (id: ICartProduct['id']) => void;
  applyPbmVerifiedPrices: (prices: CartVerifyItemResponse[]) => void;
  revertPbmComboPrices: () => void;
  revertInterplayersPbmToCatalog: () => void;
  /** Call once cross-device bootstrap (or post-login skip path) finished so background sync may run */
  onCartBootstrapComplete: () => void;
}

export const cartContext = React.createContext<CartProviderState | undefined>(
  undefined,
);

cartContext.displayName = 'CartContext';

export const useCart = () => {
  const context = React.useContext(cartContext);
  if (context === undefined) {
    throw new Error(`useCart must be used within a CartProvider`);
  }
  return React.useMemo(() => context, [context]);
};

export const CartProvider: React.FC<{ children?: React.ReactNode }> = (
  props,
) => {
  const [savedCart, saveCart] = useLocalStorage(
    CART_KEY,
    JSON.stringify(initialState),
  );
  const { settings } = useSettings();
  const [isAuthorized] = useAtom(authorizationAtom);

  /** When false and logged in, block debounced POST so an empty cart cannot race GET/merge on a new browser */
  const [cartBootstrapDone, setCartBootstrapDone] = React.useState(() =>
    typeof window === 'undefined' ? true : !checkIsLoggedIn(),
  );

  /** Only reset the bootstrap gate on a real login (anonymous → authorized), not on every remount while already logged in */
  const prevAuthorizedRef = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    const prev = prevAuthorizedRef.current;
    prevAuthorizedRef.current = isAuthorized;
    if (!isAuthorized) {
      setCartBootstrapDone(true);
      return;
    }
    if (prev === false && isAuthorized === true) {
      setCartBootstrapDone(false);
    }
  }, [isAuthorized]);

  const onCartBootstrapComplete = useCallback(() => {
    setCartBootstrapDone(true);
  }, []);

  const cartBootstrapDoneRef = React.useRef(cartBootstrapDone);
  cartBootstrapDoneRef.current = cartBootstrapDone;

  const [state, dispatch] = React.useReducer(
    cartReducer,
    { settings, savedCart },
    ({ settings: s, savedCart: raw }) => {
      if (!raw) return { settings: s, ...initialState };
      try {
        const p = JSON.parse(raw);
        return {
          settings: s,
          ...p,
          items: normalizeCartLineTimestamps(p.items ?? []),
        };
      } catch {
        return { settings: s, ...initialState };
      }
    },
  );

  const [, emptyVerifiedResponse] = useAtom(verifiedResponseAtom);

  const stateRef = React.useRef(state);
  stateRef.current = state;

  const langRef = React.useRef(state.language);
  langRef.current = state.language;

  const queueRef = React.useRef<ReturnType<typeof createCartSyncQueue> | null>(
    null,
  );

  /** Used so we only DELETE /cart after the user had lines and removed them — never on bootstrap empty */
  const prevCartItemCountRef = React.useRef(0);

  React.useEffect(() => {
    emptyVerifiedResponse(null);
  }, [emptyVerifiedResponse, state]);

  // Debounced localStorage writes stay off the critical INP path.
  // Timers live in a ref so state updates only reschedule — they do not
  // tear down pagehide/visibility listeners (those mount once below).
  const persistTimersRef = React.useRef<{
    timeoutId: ReturnType<typeof setTimeout> | null;
    idleId: number | null;
  }>({ timeoutId: null, idleId: null });
  const saveCartRef = React.useRef(saveCart);
  saveCartRef.current = saveCart;

  const persistCartToStorage = React.useCallback(() => {
    saveCartRef.current(JSON.stringify(stateRef.current));
  }, []);

  const flushCartPersist = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const timers = persistTimersRef.current;
    if (timers.timeoutId) {
      clearTimeout(timers.timeoutId);
      timers.timeoutId = null;
    }
    if (
      timers.idleId != null &&
      typeof window.cancelIdleCallback === 'function'
    ) {
      window.cancelIdleCallback(timers.idleId);
      timers.idleId = null;
    }
    persistCartToStorage();
  }, [persistCartToStorage]);

  const scheduleCartPersist = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const timers = persistTimersRef.current;
    if (timers.timeoutId) clearTimeout(timers.timeoutId);
    if (
      timers.idleId != null &&
      typeof window.cancelIdleCallback === 'function'
    ) {
      window.cancelIdleCallback(timers.idleId);
      timers.idleId = null;
    }
    timers.timeoutId = setTimeout(() => {
      timers.timeoutId = null;
      if (typeof window.requestIdleCallback === 'function') {
        timers.idleId = window.requestIdleCallback(
          () => {
            timers.idleId = null;
            persistCartToStorage();
          },
          { timeout: 1000 },
        );
      } else {
        persistCartToStorage();
      }
    }, 150);
  }, [persistCartToStorage]);

  // Reschedule debounce whenever cart state changes (reads latest via stateRef).
  React.useEffect(() => {
    scheduleCartPersist();
  }, [state, scheduleCartPersist]);

  // Flush listeners once — must not rebind on every cart mutation.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const onVis = () => {
      if (document.visibilityState === 'hidden') flushCartPersist();
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', flushCartPersist);

    return () => {
      flushCartPersist();
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', flushCartPersist);
    };
  }, [flushCartPersist]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onClear = () => {
      clearCartSession();
      dispatch({
        type: 'RESET_CART_PRESERVE_LANGUAGE',
        language: langRef.current,
      });
    };
    window.addEventListener('qualidoc-cart-clear', onClear);
    return () => window.removeEventListener('qualidoc-cart-clear', onClear);
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!queueRef.current) {
      queueRef.current = createCartSyncQueue({
        getPayload: () => serializeCartForSync(stateRef.current.items),
        isLoggedIn: () => checkIsLoggedIn(),
        canFlush: () =>
          !checkIsLoggedIn() || cartBootstrapDoneRef.current,
      });
    }
    const q = queueRef.current;
    /** Brief delay avoids flushing while the tab is only momentarily backgrounded (reduces aborted /cart/sync bodies). */
    const VISIBILITY_FLUSH_DELAY_MS = 150;
    let hiddenFlushTimer: ReturnType<typeof setTimeout> | null = null;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (hiddenFlushTimer) clearTimeout(hiddenFlushTimer);
        hiddenFlushTimer = setTimeout(() => {
          hiddenFlushTimer = null;
          q.flushNow();
        }, VISIBILITY_FLUSH_DELAY_MS);
      } else if (hiddenFlushTimer) {
        clearTimeout(hiddenFlushTimer);
        hiddenFlushTimer = null;
      }
    };
    const onUnload = () => {
      if (hiddenFlushTimer) {
        clearTimeout(hiddenFlushTimer);
        hiddenFlushTimer = null;
      }
      q.flushKeepalive();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('beforeunload', onUnload);
    return () => {
      if (hiddenFlushTimer) clearTimeout(hiddenFlushTimer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !queueRef.current) return;
    if (!isAuthorized || !cartBootstrapDone) {
      if (!isAuthorized) prevCartItemCountRef.current = 0;
      return;
    }
    const q = queueRef.current;
    const prev = prevCartItemCountRef.current;
    const n = state.items.length;
    prevCartItemCountRef.current = n;
    if (n === 0) {
      q.cancelPendingFlush();
      if (prev > 0) {
        void deleteServerCart().catch(() => {});
      }
      return;
    }
    q.scheduleFlush();
  }, [isAuthorized, cartBootstrapDone, state.items]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (state.items.length > 0) {
      ensureCartSession();
    }
  }, [state.items.length]);

  const addItemsToCart = useCallback(
    (items: ICartProduct[]) =>
      dispatch({ type: 'ADD_ITEMS_WITH_QUANTITY', items }),
    [],
  );
  const addItemToCart = useCallback(
    (item: ICartProduct, quantity: number) =>
      dispatch({ type: 'ADD_ITEM_WITH_QUANTITY', item, quantity }),
    [],
  );
  const removeItemFromCart = useCallback(
    (id: ICartProduct['id'], quantity = 1) =>
      dispatch({ type: 'REMOVE_ITEM_OR_QUANTITY', id, quantity }),
    [],
  );
  const clearItemFromCart = useCallback(
    (id: ICartProduct['id']) => dispatch({ type: 'REMOVE_ITEM', id }),
    [],
  );
  const isInCart = useCallback(
    (id: ICartProduct['id']) => !!getItem(state.items, id),
    [state.items],
  );
  const countItemsInCartById = useCallback(
    (id: ICartProduct['id']) => getItem(state.items, id)?.quantity || 0,
    [state.items],
  );
  const getItemFromCart = useCallback(
    (id: ICartProduct['id']) => getItem(state.items, id),
    [state.items],
  );
  const isInStock = useCallback(
    (id: ICartProduct['id']) => inStock(state.items, id),
    [state.items],
  );
  const updateCartLanguage = useCallback(
    (language: string) => dispatch({ type: 'UPDATE_CART_LANGUAGE', language }),
    [],
  );
  const resetCart = useCallback(() => {
    queueRef.current?.cancelPendingFlush();
    clearCartSession();
    flushSync(() => {
      dispatch({ type: 'RESET_CART' });
    });
  }, []);
  const hydrateMergedCart = useCallback((merged: ICartProduct[]) => {
    flushSync(() => {
      dispatch({ type: 'HYDRATE_MERGED_CART', items: merged });
    });
  }, []);
  const updateItemPrices = useCallback(
    (
      items: {
        id: string;
        price: number;
        sale_price: number;
        promotional_pack_unit_price?: number;
        product_prices?: any[];
      }[],
    ) => dispatch({ type: 'UPDATE_ITEM_PRICES', items }),
    [],
  );
  const updateItem = useCallback(
    (id: ICartProduct['id'], item: UpdateItemInput) =>
      dispatch({ type: 'UPDATE_ITEM', id, item }),
    [],
  );
  const removePBMFromItem = useCallback(
    (id: ICartProduct['id']) => dispatch({ type: 'REMOVE_PBM_FROM_ITEM', id }),
    [],
  );
  const applyPbmVerifiedPrices = useCallback(
    (prices: CartVerifyItemResponse[]) =>
      dispatch({ type: 'APPLY_PBM_VERIFIED_PRICES', prices }),
    [],
  );
  const revertPbmComboPrices = useCallback(
    () => dispatch({ type: 'REVERT_PBM_COMBO_PRICES' }),
    [],
  );
  const revertInterplayersPbmToCatalog = useCallback(
    () => dispatch({ type: 'REVERT_INTERPLAYERS_PBM_TO_CATALOG' }),
    [],
  );

  const value = React.useMemo(
    () => ({
      ...state,
      addItemsToCart,
      addItemToCart,
      removeItemFromCart,
      clearItemFromCart,
      getItemFromCart,
      isInCart,
      isInStock,
      resetCart,
      hydrateMergedCart,
      onCartBootstrapComplete,
      updateCartLanguage,
      countItemsInCartById,
      updateItemPrices,
      updateItem,
      removePBMFromItem,
      applyPbmVerifiedPrices,
      revertPbmComboPrices,
      revertInterplayersPbmToCatalog,
    }),
    [
      addItemToCart,
      addItemsToCart,
      applyPbmVerifiedPrices,
      clearItemFromCart,
      countItemsInCartById,
      getItemFromCart,
      hydrateMergedCart,
      isInCart,
      isInStock,
      onCartBootstrapComplete,
      removeItemFromCart,
      removePBMFromItem,
      resetCart,
      revertInterplayersPbmToCatalog,
      revertPbmComboPrices,
      state,
      updateCartLanguage,
      updateItem,
      updateItemPrices,
    ],
  );

  return <cartContext.Provider value={value} {...props} />;
};
